/**
 * Utilitários genéricos de navegação em payloads JSON (padrão n8n)
 * -----------------------------------------------------------------------------
 * Usados pelos nodes que consomem retorno de APIs externas (Webhook, Conexão).
 * São PROVIDER-AGNÓSTICOS: nenhuma regra específica de fornecedor aqui.
 *
 * CHANGELOG:
 *   RELEASE: 2026-08-29 v1.0.0 - Extraído de useWebhookTest.ts sem alteração de
 *            comportamento, para reuso no node "Conexão".
 */

/**
 * Extrai caminhos únicos de um objeto JSON para sugestão de mapeamento.
 * Retorna paths como "campo", "objeto.campo", "[0].campo".
 */
export function extractJsonPaths(obj: unknown, prefix = '', maxDepth = 4): string[] {
  const paths: string[] = [];

  if (maxDepth <= 0) return paths;

  if (Array.isArray(obj)) {
    // Para arrays, adiciona path com índice [0]
    if (obj.length > 0) {
      const firstItem = obj[0];
      if (typeof firstItem === 'object' && firstItem !== null) {
        const arrayPrefix = prefix ? `${prefix}[0]` : '[0]';
        paths.push(...extractJsonPaths(firstItem, arrayPrefix, maxDepth - 1));
      } else {
        paths.push(prefix ? `${prefix}[0]` : '[0]');
      }
    }
  } else if (typeof obj === 'object' && obj !== null) {
    for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
      const currentPath = prefix ? `${prefix}.${key}` : key;

      if (typeof value === 'object' && value !== null) {
        paths.push(currentPath);
        paths.push(...extractJsonPaths(value, currentPath, maxDepth - 1));
      } else {
        paths.push(currentPath);
      }
    }
  }

  return paths;
}

/**
 * Extrai valor de um objeto usando path (dot notation + índice de array).
 * Ex.: "data.user.name", "[0].id", "items[0].title"
 */
export function getValueByPath(obj: unknown, path: string): unknown {
  if (!path || obj === undefined || obj === null) return undefined;

  const normalizedPath = path
    .replace(/\[(\d+)\]/g, '.$1')
    .replace(/^\./, '');

  const parts = normalizedPath.split('.');
  let current: unknown = obj;

  for (const part of parts) {
    if (current === undefined || current === null) return undefined;

    const index = parseInt(part, 10);
    if (!isNaN(index) && Array.isArray(current)) {
      current = current[index];
    } else {
      current = (current as Record<string, unknown>)[part];
    }
  }

  return current;
}

/**
 * Converte um valor de payload em texto utilizável como variável do fluxo.
 * Objetos/arrays viram JSON; null/undefined viram string vazia.
 */
export function stringifyPathValue(value: unknown): string {
  if (value === undefined || value === null) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/**
 * Nó da árvore de inspeção de payload (usado pelo JsonExplorer).
 * RELEASE: 2026-08-29 v1.1.0 - Adicionados buildJsonTree() e suggestVariableName().
 */
export interface JsonTreeNode {
  /** Chave exibida (nome do campo ou índice do array) */
  key: string;
  /** Caminho completo até o nó (ex.: "subject.address[0].city") */
  path: string;
  /** Tipo simplificado do valor */
  type: 'object' | 'array' | 'string' | 'number' | 'boolean' | 'null';
  /** Valor bruto (apenas para folhas) */
  value?: unknown;
  /** Filhos (objetos e arrays) */
  children?: JsonTreeNode[];
}

/** Tipo simplificado de um valor JSON */
function jsonType(value: unknown): JsonTreeNode['type'] {
  if (value === null || value === undefined) return 'null';
  if (Array.isArray(value)) return 'array';
  switch (typeof value) {
    case 'object':
      return 'object';
    case 'number':
      return 'number';
    case 'boolean':
      return 'boolean';
    default:
      return 'string';
  }
}

/**
 * Constrói a árvore navegável de um payload JSON.
 * Provider-agnóstico e puro. `maxDepth` e `maxChildren` protegem a UI de
 * payloads gigantes.
 */
export function buildJsonTree(
  data: unknown,
  prefix = '',
  maxDepth = 8,
  maxChildren = 500,
): JsonTreeNode[] {
  if (maxDepth <= 0 || data === null || typeof data !== 'object') return [];

  const entries: Array<[string, unknown, string]> = Array.isArray(data)
    ? data
        .slice(0, maxChildren)
        .map((v, i) => [`[${i}]`, v, prefix ? `${prefix}[${i}]` : `[${i}]`])
    : Object.entries(data as Record<string, unknown>)
        .slice(0, maxChildren)
        .map(([k, v]) => [k, v, prefix ? `${prefix}.${k}` : k]);

  return entries.map(([key, value, path]) => {
    const type = jsonType(value);
    const node: JsonTreeNode = { key, path, type };
    if (type === 'object' || type === 'array') {
      node.children = buildJsonTree(value, path, maxDepth - 1, maxChildren);
    } else {
      node.value = value;
    }
    return node;
  });
}

/**
 * Sugere um nome de variável válido a partir de um caminho no payload.
 * Ex.: "subject.address[0].city" -> "subject_address_city"
 */
export function suggestVariableName(path: string): string {
  return path
    .replace(/\[(\d+)\]/g, '_$1')
    .replace(/[^\w]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '')
    .toLowerCase()
    .slice(0, 60);
}
